import {
  MIN_SUMMARY_LENGTH,
  MIN_SUMMARY_LENGTH_DENSE_SCRIPT,
} from './FeedbackWidget.constants'

/**
 * Deterministic checks that run before a submission is sent.
 *
 * These exist because most of the noise in the feedback form is not spam — it
 * is people who think the box does something else, and type a username, a
 * pasted link or a single word. Every rule below is intentionally mechanical:
 * it never guesses at meaning, so it cannot silently discard real feedback.
 * Anything requiring judgement is left to a human reading the row.
 */

/** Matches when the value is nothing but a URL. */
const URL_ONLY = /^(?:https?:\/\/|www\.)\S+$/i

/** Matches when the value is nothing but an email address. */
const EMAIL_ONLY = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Any letter in any script — Arabic, Bengali and Korean all count. */
const CONTAINS_LETTER = /\p{L}/u

/**
 * Only the tags an attack is delivered through, rather than any pair of angle
 * brackets. Someone writing "the <select> component keeps disappearing" is
 * describing a bug, not probing us, and the old any-tag pattern rejected them.
 */
const DANGEROUS_TAG =
  /<\s*\/?\s*(?:script|iframe|img|svg|object|embed|link|style|form|input|meta|base|a)\b/i

/** An inline handler, whether or not the tag that would carry it came too. */
const INLINE_HANDLER = /\bon[a-z]+\s*=/i

const JAVASCRIPT_URI = /javascript:/i

/**
 * Scripts where one character carries closer to a word than a letter, so a
 * character count means something different. "I cannot schedule posts" is 23
 * characters; the same sentence in Chinese is 8.
 */
const DENSE_SCRIPT =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{Script=Thai}]/u

/**
 * Punctuation and casing that make a run of characters read as an identifier or
 * a code fragment rather than words. Only applied to whitespace-free input in a
 * script that separates words, because these appear in ordinary prose too:
 * "post to X/Twitter" matches, and so does any dense-script sentence naming a
 * camelCase product — `YouTubeに投稿できません` matches on `uT`.
 */
const CODE_SHAPED = /[_;{}=]|\(\s*\)|=>|::|\.\w|\/\w|[a-z][A-Z]/

function minSummaryLength(value: string): number {
  return DENSE_SCRIPT.test(value)
    ? MIN_SUMMARY_LENGTH_DENSE_SCRIPT
    : MIN_SUMMARY_LENGTH
}

function isInjectionShaped(value: string): boolean {
  return (
    DANGEROUS_TAG.test(value) ||
    INLINE_HANDLER.test(value) ||
    JAVASCRIPT_URI.test(value)
  )
}

export type ValidationError = string | null

/** Rule names sent on the `Feedback Form Validation Failed` tracking event. */
export type SummaryValidationRule =
  | 'blank'
  | 'injectionShaped'
  | 'noLetters'
  | 'urlOrEmailOnly'
  | 'tooShort'
  | 'singleToken'

export type ContextValidationRule = 'blank' | 'injectionShaped' | 'noLetters'

interface ValidationFailure<Rule extends string> {
  rule: Rule
  message: string
}

function isBlank(value: string): boolean {
  return value.trim().length === 0
}

function checkSummary(
  value: string,
): ValidationFailure<SummaryValidationRule> | null {
  const trimmed = value.trim()

  if (isBlank(value)) {
    return { rule: 'blank', message: 'Let us know what would help.' }
  }

  if (isInjectionShaped(trimmed)) {
    return {
      rule: 'injectionShaped',
      message: 'Please remove any HTML or code from your answer.',
    }
  }

  if (!CONTAINS_LETTER.test(trimmed)) {
    return {
      rule: 'noLetters',
      message: 'Please describe this in words so we can act on it.',
    }
  }

  if (URL_ONLY.test(trimmed) || EMAIL_ONLY.test(trimmed)) {
    return {
      rule: 'urlOrEmailOnly',
      message:
        'Add a few words about what you would change — a link on its own does not tell us enough.',
    }
  }

  // Length is checked before the identifier rule so that a short value gets
  // the more useful "add more detail" message rather than being told off for
  // not being a sentence.
  const minLength = minSummaryLength(trimmed)
  if (trimmed.length < minLength) {
    return {
      rule: 'tooShort',
      message: `Please add a little more detail — at least ${minLength} characters.`,
    }
  }

  // Plenty of languages do not put spaces between words, so a single token is
  // only a problem when it reads as an identifier — which cannot be judged from
  // casing in a script that never separates words.
  if (
    !/\s/.test(trimmed) &&
    !DENSE_SCRIPT.test(trimmed) &&
    CODE_SHAPED.test(trimmed)
  ) {
    return {
      rule: 'singleToken',
      message: 'Please describe what would help in a sentence or two.',
    }
  }

  return null
}

function checkContext(
  value: string,
): ValidationFailure<ContextValidationRule> | null {
  const trimmed = value.trim()

  if (isBlank(value)) {
    return { rule: 'blank', message: 'Let us know what you were trying to do.' }
  }

  if (isInjectionShaped(trimmed)) {
    return {
      rule: 'injectionShaped',
      message: 'Please remove any HTML or code from your answer.',
    }
  }

  if (!CONTAINS_LETTER.test(trimmed)) {
    return {
      rule: 'noLetters',
      message: 'Please describe this in words so we can act on it.',
    }
  }

  return null
}

/**
 * Validates the summary field — the one that becomes the suggestion title.
 * Returns a message to show the person, or null when the value is acceptable.
 */
export function validateSummary(value: string): ValidationError {
  return checkSummary(value)?.message ?? null
}

/** Rule name for the tracking event, or null when the value is acceptable. */
export function getSummaryValidationRule(
  value: string,
): SummaryValidationRule | null {
  return checkSummary(value)?.rule ?? null
}

/**
 * Validates the context field — the one that becomes the description. It has no
 * minimum length, because "post a video to TikTok" is a complete answer.
 */
export function validateContext(value: string): ValidationError {
  return checkContext(value)?.message ?? null
}

/** Rule name for the tracking event, or null when the value is acceptable. */
export function getContextValidationRule(
  value: string,
): ContextValidationRule | null {
  return checkContext(value)?.rule ?? null
}
